import React, {useCallback, useEffect, useState} from 'react'
import H from 'history'
import {useLocation} from 'react-router-dom'
import {MemberJoinInfo} from '../../../Props/MembersJoinPageStates'
import {RegisterOrganizationContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'
import {BasicMemberInfoConfirmContents} from '../../Components/ConfirmContentBlocks/BasicMemberInfoConfirmContents'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import MemberRepo from '../../../Repo/MemberRepo'
import {MBMemberError} from '../../../Common/MBMemberError'
import {MemberAddressDTO} from '../../../DTO/MemberAddressDTO'
import {useMBMemberError} from '../../Hooks/UseMBMemberError'
import {BucketContractInputInfoConvenienceStore, BucketContractInputInfoCreditCard, BucketContractInputInfoWithoutPayment, MemberJoinBucketDTO} from '../../../DTO/MemberJoinBucketDTO'
import {MailMagazineConfirmContentBlock} from '../../Components/ConfirmContentBlocks/MailMagazineConfirmContentBlock'
import {animateScroll} from 'react-scroll'
import {PaymentMethod} from '../../../DTO/PaymentMethodDTO'
import {ContractInputInfo, UsingMyCreditCardInfo} from '../../../Props/ContractInputInfo'
import {Nullable, Optional} from '../../../../Common/TypeHelper'
import {TokenResponse, useGmo} from '../../Hooks/UseGmo'
import {CommonResponseStatus} from '../../../../Network/CommonResponse'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {ContentBlock} from '../../Molecules/BaseContentBlocks/ContentBlock'
import {ContentTitle} from '../../Molecules/BaseContentBlocks/ContentTitle'
import {MessageForAction} from '../../Molecules/BaseMembersPages/MessageForAction'
import {ErrorDisplay} from '../../Molecules/ErrorsAndAlert/ErrorDisplay'
import {ContractTermConfirmContent} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractTermConfirmContent'
import {ContractPaymentMethodConfirm} from '../../Components/PaymentsRelations/ConfirmContentBlocks/ContractPaymentMethodConfirm'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import {PIMSecondaryButton} from '../../Atoms/Buttons/Bases/PIMSecondaryButton'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {Spacer} from '../../../../Common/Components/Spacer'
import {MemberPreJoinBucketDTO} from '../../../DTO/MemberPreJoinBucketDTO'
import {PAYMENT} from '../../../../Common/Constants'
import {ContactAddressConfirmContents} from '../../Components/ConfirmContentBlocks/AddressConfirms/ContactAddressConfirmContents'
import {BucketRegisterResponseDataDTO, FreeBucketRegisterResponseDataDTO, isFreeBucketContract} from '../../../DTO/BucketRegisterResponseDataDTO'
import {CvsContractDTO, isCvsContract} from '../../../DTO/CvsContractDTO'
import {EnrollmentReasonConfirmContentBlock} from '../../Components/ConfirmContentBlocks/EnrollmentReasonConfirmContentBlock'
import {SignedUpSignboard} from '../../../DTO/SignedUpSignboardDTO'
import {useTranslation} from 'react-i18next'

type Props = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
}

export type JoinConfirmLocationState = {
  memberInfo: MemberJoinInfo
  signboard: SignedUpSignboard
  paymentMethodList: PaymentMethod[],
  myCreditCardInfoList: UsingMyCreditCardInfo[],
  contractInputInfo: ContractInputInfo,
}

export const MembersJoinInputConfirmPage = (props: Props): Nullable<JSX.Element> => {
  const scriptUrl: Optional<string> = process.env.REACT_APP_MEMBUCKET_ADMIN_APP_GMO_SCRIPT_URL
  const timeout: Optional<number> =
    parseInt(process.env.REACT_APP_MEMBUCKET_ADMIN_APP_GMO_SCRIPT_TIMEOUT ?? '5000', 10)

  const {t} = useTranslation('member')
  const [tokenResponse, setTokenResponse] = useState<TokenResponse>()

  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [processing, setProcessing] = useState<boolean>(false)

  if (!scriptUrl) {
    throw new MBMemberError('SystemError')
  }
  const [loading, error, gmo] = useGmo(scriptUrl)

  const location: H.Location<JoinConfirmLocationState> = useLocation<JoinConfirmLocationState>()
  const locationState: JoinConfirmLocationState = location.state

  const [gmoTimeoutErrorFlg, setGmoTimeoutErrorFlg] = useState(false)

  const throwMBMemberError = useMBMemberError()

  const goToJoinCompletePage = useCallback(
    async (): Promise<void> => {
      if (!locationState) {
        return
      }
      setProcessing(true)

      try {
        const putAddressResult = await props.memberRepo.putAddress(new MemberAddressDTO(
            locationState.memberInfo.postCode,
            locationState.memberInfo.addressPrefectureCode,
            locationState.memberInfo.addressCity,
            locationState.memberInfo.addressStreet,
            locationState.memberInfo.addressBuilding,
            locationState.memberInfo.phoneMobile,
            locationState.memberInfo.phoneTel,
          ),
          false,
        )

        switch (putAddressResult.status) {
          case CommonResponseStatus.Ok:
            break
          case CommonResponseStatus.BadRequest:
            setProcessing(false)
            setErrorMessages(putAddressResult.errorMessageList)
            animateScroll.scrollToTop()
            return
          default:
            // noinspection ExceptionCaughtLocallyJS
            throw new MBMemberError('UnknownError')
        }
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'SystemError'
        throwMBMemberError(errorMessage)
      }

      const {term, method, creditCard, mobileCarrier} = locationState.contractInputInfo

      if (method === 'credit_card' || method === 'convenience_store' || method === 'none') {
        const selectedMyCreditCardInfo =
          creditCard?.myCreditCardInfo.securityCodeMap
            .find(item => item.cardId === creditCard.myCreditCardInfo.selectedCardId)

        await props.memberRepo.postBelongToBucket(
          new MemberJoinBucketDTO(
            locationState.signboard.signupKey,
            locationState.memberInfo.agreedIdList,
            locationState.memberInfo.mailMagazineIdCheckedList,
            getContractInfoForPostBelongToBucket(
              contractInputInfo,
              selectedMyCreditCardInfo?.securityCode,
              tokenResponse?.tokenObject.token,
            ),
            locationState.memberInfo.enrollmentReasonSelectedInfo,
          ))
          .then((postBelongToBucketResult) => {
            switch (postBelongToBucketResult.status) {
              case CommonResponseStatus.Created:
                if (!postBelongToBucketResult.data) {
                  // noinspection ExceptionCaughtLocallyJS
                  throw new MBMemberError('SystemError')
                }
                gotoCompletePage(props.navigator, locationState, postBelongToBucketResult.data)
                break
              case CommonResponseStatus.NotFound:
                // noinspection ExceptionCaughtLocallyJS
                throw new MBMemberError('NotFound')
              case CommonResponseStatus.Conflict:
                // noinspection ExceptionCaughtLocallyJS
                throw new MBMemberError('Conflict')
              case CommonResponseStatus.InvalidRequest:
                // noinspection ExceptionCaughtLocallyJS
                throw new MBMemberError('ServerError')
              case CommonResponseStatus.InvalidCreditCard:
                props.navigator.backToMembersJoinInputPageCauseCreditCardError(
                  locationState.memberInfo,
                  locationState.signboard,
                  locationState.paymentMethodList,
                  locationState.myCreditCardInfoList,
                  contractInputInfo,
                )
                return
              case CommonResponseStatus.PaymentSystemError:
                // noinspection ExceptionCaughtLocallyJS
                throw new MBMemberError('SystemError')
              default:
                // noinspection ExceptionCaughtLocallyJS
                throw new MBMemberError('UnknownError')
            }
          })
          .catch((e) => {
            const errorMessage = e instanceof Error ? e.message : 'SystemError'
            throwMBMemberError(errorMessage)
          })
      }

      if (method === 'mobile_carrier') {
        await props.memberRepo.postPreBelongToBucket(new MemberPreJoinBucketDTO(
          locationState.signboard.signupKey,
          locationState.memberInfo.agreedIdList,
          locationState.memberInfo.mailMagazineIdCheckedList,
          {
            contractTerm: term,
            paymentMethod: method,
            paymentMethodDetail: mobileCarrier.carrierKey,
          },
          locationState.memberInfo.enrollmentReasonSelectedInfo,
        ))
          .then((postPreBelongToBucketResult) => {
            switch (postPreBelongToBucketResult.status) {
              case CommonResponseStatus.Created:
                if (!postPreBelongToBucketResult.data) {
                  throw new MBMemberError('SystemError')
                }

                props.navigator.goToMembersJoinMobileCarrierTransitionPage(
                  {...postPreBelongToBucketResult.data},
                )
                break
              case CommonResponseStatus.BadRequest:
                setErrorMessages(postPreBelongToBucketResult.errorMessageList)
                setProcessing(false)
                break
              case CommonResponseStatus.NotFound:
                throw new MBMemberError('NotFound')
              case CommonResponseStatus.InvalidRequest:
                throw new MBMemberError('ServerError')
              case CommonResponseStatus.Conflict:
                throw new MBMemberError('Conflict')
              case CommonResponseStatus.InternalServerError:
                throw new MBMemberError('ServerError')
              case CommonResponseStatus.PaymentSystemError:
                throw new MBMemberError('SystemError')
              default:
                throw new MBMemberError('ServerError')
            }
          })
          .catch((e) => {
            const errorMessage = e instanceof Error ? e.message : 'SystemError'
            throwMBMemberError(errorMessage)
          })
      }
    },
    [
      props.memberRepo,
      props.navigator,
      locationState,
      tokenResponse,
    ])

  useEffect(() => {
    if (!locationState?.contractInputInfo) {
      return
    }
    if (locationState.contractInputInfo.creditCard?.creditCardType === 'myCard') {
      return
    }

    if (gmo) {
      if (locationState.contractInputInfo.method !== 'credit_card') {
        return
      }
      if (!locationState.contractInputInfo.creditCard) {
        return
      }

      const shopCode = locationState.signboard.shopId
      if (!shopCode) {
        throw new MBMemberError('SystemError')
      }

      const {cardNumber, expireMonth, expireYear, holderName, securityCode} =
        locationState.contractInputInfo.creditCard.newCreditCardInfo

      gmo.init(shopCode)

      const timeoutId = setTimeout(() => {
        setGmoTimeoutErrorFlg(true)
      }, timeout)

      gmo.getToken({
        tokennumber: 2,
        cardno: cardNumber,
        expire: expireYear.slice(-2) + expireMonth,
        securitycode: securityCode,
        holdername: holderName,
      }, (result: TokenResponse) => {
        clearTimeout(timeoutId)
        setTokenResponse(result)
      })
    }
  }, [gmo])

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  if (locationState.contractInputInfo.method === 'credit_card' &&
    locationState.contractInputInfo.creditCard?.creditCardType === 'newCard') {
    if (gmoTimeoutErrorFlg) {
      throw new MBMemberError('SystemError')
    }

    if (error) {
      throw new MBMemberError('SystemError')
    }
    if (loading) {
      return null
    }
    if (!gmo) {
      return null
    }

    if (!tokenResponse) {
      return null
    }
  }

  if (!locationState.contractInputInfo.creditCard) {
    throw new MBMemberError('SystemError')
  }

  const {
    signboard,
    memberInfo,
    contractInputInfo,
    myCreditCardInfoList,
  } = locationState
  const {
    mail,
    familyName, firstName,
    familyNameKana, firstNameKana,
    gender,
    birthday,
    postCode,
    addressPrefectureCode, addressCity, addressStreet, addressBuilding,
    phoneMobile, phoneTel,
    mailMagazineIdCheckedList,
    enrollmentReasonSelectedInfo,
  } = memberInfo
  const {term, method, amount} = contractInputInfo

  const registerButtonLabel = locationState.contractInputInfo.method === 'mobile_carrier' ?
    '決済に進む' : '確定・入会する'

  return (
    <MembersPageTemplate hideFooterNavi={true}>
      <PageTitle>{`${signboard.signupName} 新規会員情報確認`}</PageTitle>

      <StepGuide activity={'signup'} current={'confirm'}/>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <RegisterOrganizationContentBlock
            signboard={signboard}
            title={t('新規会員情報確認.入会バケット情報.見出し')}
            paymentInformationList={[{
              isFreeBucket: method === 'none',
              termName: PAYMENT.TERMS[term].displayLabel,
              contractAmount: amount,
              order: PAYMENT.TERMS[term].order,
              term: term,
            }]}
          />
        </PIMStack>
      </Spacer>

      <Spacer mb={'16px'}>
        <PIMStack alignItems={'center'}>
          <BasicMemberInfoConfirmContents
            mail={mail}
            familyName={familyName}
            firstName={firstName}
            familyNameKana={familyNameKana}
            firstNameKana={firstNameKana}
            gender={gender}
            birthday={birthday}
          />
        </PIMStack>
      </Spacer>

      <ContentBlock data-testid={'block-confirms'}>
        <PIMStack spacing={'18px'}>
          <ContentTitle
            noMarginBottom={true}>{t('新規会員情報確認.連絡先情報.見出し')}</ContentTitle>
          <ContactAddressConfirmContents
            postCode={postCode}
            addressPrefectureCode={addressPrefectureCode}
            addressCity={addressCity}
            addressStreet={addressStreet}
            addressBuilding={addressBuilding}
            phoneMobile={phoneMobile}
            phoneTel={phoneTel}
          />
        </PIMStack>
      </ContentBlock>

      <MailMagazineConfirmContentBlock
        title={'メルマガ情報'}
        mailMagazineIdCheckedList={mailMagazineIdCheckedList}
        mailMagazineList={signboard.mailMagazineList}
      />

      {!signboard.mainService.isTakeover && <EnrollmentReasonConfirmContentBlock
          enrollmentReasonInfoList={signboard.mainService.enrollmentReasonList}
          enrollmentReasonSelectedInfo={enrollmentReasonSelectedInfo}
      />}

      {method !== 'none' && <>
          <ContractTermConfirmContent
              title={'契約単位'}
              term={term}
              contractMethod={method}
          />

          <ContractPaymentMethodConfirm
              title={'お支払い方法'}
              amount={amount}
              method={method}
              methodDetail={{
                paymentInputDetailWithMethod: contractInputInfo,
                myCreditCardInfoList: myCreditCardInfoList,
                maskedCardNo: tokenResponse?.tokenObject.maskedCardNo,
              }}
              isTakeover={signboard.mainService.isTakeover}
          />
      </>
      }

      <MessageForAction>
        上記の内容に間違いがなければ<br/>
        {`『${registerButtonLabel}』ボタンを押してください。`}
      </MessageForAction>

      <PIMStack alignItems={'center'}>
        <ErrorDisplay errors={errorMessages}/>
      </PIMStack>

      <PIMPageActions>
        <PIMSubmitButton
          testId={'confirm-join-button'}
          disabled={processing}
          onClick={(): Promise<void> => goToJoinCompletePage()}
        >
          {registerButtonLabel}
        </PIMSubmitButton>
        <PIMSecondaryButton onClick={(): void => props.navigator.goBack()}>
          会員情報入力に戻る
        </PIMSecondaryButton>
      </PIMPageActions>
    </MembersPageTemplate>
  )
}

const isInvalidLocationState = (locationState: JoinConfirmLocationState): boolean => {
  if (!locationState) {
    return true
  }
  if (!locationState.memberInfo) {
    return true
  }
  if (!locationState.signboard) {
    return true
  }
  if (!locationState.myCreditCardInfoList) {
    return true
  }
  if (!locationState.paymentMethodList) {
    return true
  }
  if (!locationState.contractInputInfo) {
    return true
  }
  if (locationState.memberInfo) {
    const {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      phoneMobile, phoneTel, addressBuilding, enrollmentReasonSelectedInfo,
      ...rest
    } = locationState.memberInfo
    if (phoneMobile === undefined && phoneTel === undefined) {
      return true
    }
    if (Object.values(rest).some(value => value === undefined)) {
      return true
    }
  }
  if (!locationState.signboard.signupName ||
    !locationState.signboard.mainService ||
    !locationState.signboard.freeServices ||
    !locationState.signboard.mailMagazineList ||
    !locationState.signboard.logoImgUrl
  ) {
    return true
  }
  return false
}

const getContractInfoForPostBelongToBucket = (
  contractInfo: Pick<ContractInputInfo, 'term' | 'method' | 'creditCard' | 'convenienceStore'>,
  securityCode?: string,
  tokenList?: string[],
): BucketContractInputInfoCreditCard |
  BucketContractInputInfoConvenienceStore |
  BucketContractInputInfoWithoutPayment => {
  const {term, method, creditCard, convenienceStore} = contractInfo

  if (method === 'none') {
    return {
      contractTerm: term,
      paymentMethod: method,
    }
  }

  if (method === 'convenience_store') {
    return {
      contractTerm: term,
      paymentMethod: 'convenience_store',
      paymentMethodDetail: convenienceStore.convenienceStoreKey,
    }
  }

  if (creditCard.creditCardType === 'newCard') {
    return {
      contractTerm: term,
      paymentMethod: 'credit_card',
      tokenList: tokenList || [],
      creditCardExpireDate:
        `${creditCard.newCreditCardInfo.expireMonth}/${creditCard.newCreditCardInfo.expireYear}`,
      cardId: undefined,
      securityCode: undefined,
    }
  }

  return {
    contractTerm: term,
    paymentMethod: 'credit_card',
    tokenList: [],
    creditCardExpireDate: undefined,
    cardId: creditCard.myCreditCardInfo.selectedCardId,
    securityCode: securityCode,
  }
}

const gotoCompletePage = (
  navigator: NavigatorForMemberPage,
  locationState: JoinConfirmLocationState,
  postBelongToBucketResultData: BucketRegisterResponseDataDTO |
    CvsContractDTO |
    FreeBucketRegisterResponseDataDTO,
): void => {
  if (isCvsContract(postBelongToBucketResultData)) {
    navigator.goToJoinCompletePageForConvenienceStore(
      locationState.memberInfo,
      locationState.signboard,
      {
        term: locationState.contractInputInfo.term,
        method: locationState.contractInputInfo.method,
        amount: locationState.contractInputInfo.amount,
        contractDate: {
          contractDate: postBelongToBucketResultData.contractDate,
          contractEndDate: postBelongToBucketResultData.contractEndDate,
        },
      },
      postBelongToBucketResultData,
    )
  } else if (isFreeBucketContract(postBelongToBucketResultData)) {
    navigator.goToJoinCompletePageForFreeBucket(
      locationState.memberInfo,
      locationState.signboard,
      postBelongToBucketResultData.bucketMemberIdList,
    )
  } else {
    navigator.goToJoinCompletePage(
      locationState.memberInfo,
      locationState.signboard,
      postBelongToBucketResultData.bucketMemberIdList,
      {
        term: locationState.contractInputInfo.term,
        method: locationState.contractInputInfo.method,
        amount: locationState.contractInputInfo.amount,
        contractDate: {
          contractDate: postBelongToBucketResultData.contractDate,
          contractEndDate: postBelongToBucketResultData.contractEndDate,
        },
      },
    )
  }
}
